import { css, styled } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { MEGA_NAV_GEOMETRY } from './megaNav.geometry';

/** Horizontal strip of the five slots, scrollable when they don't fit (mirrors the flat bar's overflow). */
export const Strip = styled.nav`
  width: 100%;
  border-bottom: 1px solid ${({ theme }) => theme.colors.mono3};
  background-color: ${({ theme }) => theme.colors.white};
`;

export const StripList = styled.ul`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space[MEGA_NAV_GEOMETRY.stripGapSpace]};
  margin: 0;
  padding: ${({ theme }) => theme.space[MEGA_NAV_GEOMETRY.stripPaddingYSpace]} ${({ theme }) => theme.space[MEGA_NAV_GEOMETRY.stripPaddingXSpace]};
  list-style: none;
  overflow-x: auto;
  white-space: nowrap;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;
  &::-webkit-scrollbar {
    display: none;
  }
`;

export const StripItem = styled.li`
  flex: 0 0 auto;
`;

/**
 * Text tab, matching the desktop bar: mono10, or mono14 with a 2px underline on the strip's bottom edge
 * when active. A chevron signals that a tap opens the sheet, not navigates. The transparent border keeps
 * the tab the width the placeholder reserves.
 */
export const StripChip = styled.button<{ $isActive?: boolean }>`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.space[MEGA_NAV_GEOMETRY.chipIconGapSpace]};
  height: ${pxToRem(MEGA_NAV_GEOMETRY.chipHeightPx)};
  padding: 0 ${pxToRem(MEGA_NAV_GEOMETRY.chipPaddingRightPx)} 0 ${pxToRem(MEGA_NAV_GEOMETRY.chipPaddingLeftPx)};
  border: 1px solid transparent;
  border-radius: ${pxToRem(8)};
  background-color: transparent;
  color: ${({ theme, $isActive }) => ($isActive ? theme.colors.mono14 : theme.colors.mono10)};
  font: inherit;
  font-size: ${pxToRem(MEGA_NAV_GEOMETRY.chipFontSizePx)};
  font-weight: 600;
  line-height: 1;
  white-space: nowrap;
  cursor: pointer;

  /* Non-text control focus contract (AGENTS.md): 2px inputBorderFocused ring, offset 2px. */
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.inputBorderFocused};
    outline-offset: 2px;
  }

  ${({ $isActive, theme }) =>
    $isActive &&
    css`
      &::after {
        content: '';
        position: absolute;
        left: ${pxToRem(MEGA_NAV_GEOMETRY.chipPaddingLeftPx)};
        right: ${pxToRem(MEGA_NAV_GEOMETRY.chipPaddingRightPx)};
        bottom: calc(-1 * ${theme.space[MEGA_NAV_GEOMETRY.stripPaddingYSpace]} - 1px);
        height: 2px;
        background-color: ${theme.colors.mono14};
      }
    `}
`;

export const SheetBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space[7]};
  padding: ${({ theme }) => theme.space[5]} ${({ theme }) => theme.space[6]} ${({ theme }) => theme.space[8]};
`;

/** The slot's own destination, given prominence since a chip tap can't navigate. */
export const SheetAllLink = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: ${({ theme }) => theme.space[5]};
  border-bottom: 1px solid ${({ theme }) => theme.colors.mono3};
  font-size: ${pxToRem(16)};
  font-weight: 600;

  a {
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.space[1]};
    color: ${({ theme }) => theme.colors.mono14};
    text-decoration: none;
  }
`;

export const SheetSection = styled.section`
  display: flex;
  flex-direction: column;
`;

/** Rows in two columns so a nine-item style list doesn't turn the sheet into a scroll marathon. */
export const SheetList = styled.ul`
  display: grid;
  grid-template-columns: 1fr 1fr;
  row-gap: ${({ theme }) => theme.space[4]};
  column-gap: ${({ theme }) => theme.space[5]};
  margin: 0;
  padding: 0;
  list-style: none;
`;

export const SheetTileGrid = styled.div<{ $columns: number }>`
  display: grid;
  grid-template-columns: repeat(${({ $columns }) => $columns}, 1fr);
  gap: ${({ theme }) => theme.space[4]};

  a {
    width: auto;
  }
  img {
    height: ${pxToRem(96)};
  }
`;
